/**
 * discoverSections.jsx — sections re-located from /explorer into the
 * "Découvrir" (Films) page. Each component is a direct port of the
 * corresponding block of Explorer.jsx, so the behavior (endpoints,
 * card layouts, search results) is identical.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, X, Film, Tv, Newspaper, Play, Volume2, VolumeX, Loader2, ExternalLink, Pause, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { HorizontalScrollSection, SearchResultsDropdown, FreeFilmCard } from '../pages/Explorer';
import CommentsModal from './reactions/CommentsModal';

const DISCOVER_COMMON = {
  fr: { comments: 'Commenter', showMore: 'Voir plus', showLess: 'Voir moins' },
  en: { comments: 'Comment', showMore: 'See more', showLess: 'See less' },
  es: { comments: 'Comentar', showMore: 'Ver más', showLess: 'Ver menos' },
  ar: { comments: 'تعليق', showMore: 'عرض المزيد', showLess: 'عرض أقل' },
  pt: { comments: 'Comentar', showMore: 'Ver mais', showLess: 'Ver menos' },
  de: { comments: 'Kommentieren', showMore: 'Mehr anzeigen', showLess: 'Weniger anzeigen' },
  ru: { comments: 'Комментировать', showMore: 'Показать больше', showLess: 'Свернуть' },
  zh: { comments: '评论', showMore: '查看更多', showLess: '收起' },
  ja: { comments: 'コメント', showMore: 'もっと見る', showLess: '閉じる' },
  hi: { comments: 'टिप्पणी करें', showMore: 'और देखें', showLess: 'कम देखें' },
  nl: { comments: 'Reageren', showMore: 'Meer tonen', showLess: 'Minder tonen' },
  sw: { comments: 'Toa maoni', showMore: 'Ona zaidi', showLess: 'Ona kidogo' },
  ht: { comments: 'Kòmante', showMore: 'Wè plis', showLess: 'Wè mwens' },
};
const dcT = (lang, k) => (DISCOVER_COMMON[lang] || DISCOVER_COMMON.en)[k];


// ===== SEARCH =====
export const SearchSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        axios.get(`/api/explorer/search?q=${encodeURIComponent(searchQuery)}`)
          .then(r => setSearchResults(r.data))
          .catch(() => setSearchResults(null));
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div className="relative mb-6">
      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={t('explorer.searchPlaceholder') || 'Rechercher…'}
        className="w-full pl-12 pr-10 py-3 bg-white/10 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
        data-testid="discover-search-input"
      />
      {searchQuery && (
        <button
          onClick={() => { setSearchQuery(''); setSearchResults(null); }}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      )}
      {searchResults && (
        <SearchResultsDropdown
          results={searchResults}
          onClose={() => setSearchResults(null)}
          navigate={navigate}
        />
      )}
    </div>
  );
};

// ===== WHERE TO WATCH =====
export const WhereToWatchSection = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [whereToWatch, setWhereToWatch] = useState({ country: 'FR', movies: [], tv: [] });
  const [selectedWatch, setSelectedWatch] = useState(null);
  const [loadingWatch, setLoadingWatch] = useState(false);

  useEffect(() => {
    const userCountry = user?.preferred_country;
    const url = userCountry
      ? `/api/explorer/where-to-watch/popular?country=${userCountry}`
      : '/api/explorer/where-to-watch/popular';
    axios.get(url).then(r => setWhereToWatch(r.data)).catch(() => {});
  }, [user, language]);

  const openWatchProviders = async (item) => {
    setLoadingWatch(true);
    setSelectedWatch({ item, providers: null });
    try {
      const userCountry = user?.preferred_country || whereToWatch.country;
      const res = await axios.get(`/api/explorer/where-to-watch?type=${item.type}&id=${item.id}&country=${userCountry}`);
      setSelectedWatch({ item, providers: res.data });
    } catch {
      toast.error('Impossible de charger les plateformes');
      setSelectedWatch(null);
    } finally {
      setLoadingWatch(false);
    }
  };

  const items = [...(whereToWatch.movies || []), ...(whereToWatch.tv || [])];

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Tv className="w-6 h-6 text-cyan-400" />
          {t('explorer.whereToWatch') || 'Où regarder ?'}
        </h2>
        <span className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded-full">📍 {whereToWatch.country}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">Chargement...</p>
      ) : (
        <HorizontalScrollSection>
          {items.map((item) => (
            <button
              key={`${item.type}-${item.id}`}
              onClick={() => openWatchProviders(item)}
              className="flex-shrink-0 w-[140px] sm:w-[160px] snap-start text-left group"
              data-testid={`watch-card-${item.id}`}
            >
              <div className="aspect-[2/3] rounded-xl overflow-hidden bg-white/5 border border-white/10 group-hover:border-cyan-500/50 transition-all relative">
                {item.poster_url ? (
                  <img src={item.poster_url} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600"><Film className="w-10 h-10" /></div>
                )}
                <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] uppercase font-bold text-cyan-300">
                  {item.type === 'tv' ? 'Série' : 'Film'}
                </div>
                {item.vote_average ? (
                  <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-yellow-500/90 rounded text-[10px] font-bold text-black">
                    ⭐ {item.vote_average}
                  </div>
                ) : null}
              </div>
              <p className="text-xs sm:text-sm mt-2 font-medium truncate">{item.title}</p>
            </button>
          ))}
        </HorizontalScrollSection>
      )}
      {selectedWatch && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-center justify-center p-4" onClick={() => setSelectedWatch(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2"><Tv className="w-5 h-5 text-cyan-400" />{t('explorer.whereToWatch') || 'Où regarder'}</h3>
              <button onClick={() => setSelectedWatch(null)} className="p-2 hover:bg-white/10 rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-3">
                {selectedWatch.item.poster_url && <img src={selectedWatch.item.poster_url} alt="" className="w-20 h-28 object-cover rounded-lg" />}
                <div>
                  <h4 className="font-bold">{selectedWatch.item.title}</h4>
                  {selectedWatch.item.vote_average ? <p className="text-xs text-yellow-400 mt-1">⭐ {selectedWatch.item.vote_average}</p> : null}
                </div>
              </div>
              {loadingWatch && <p className="text-center text-gray-400 py-4"><Loader2 className="w-5 h-5 animate-spin inline" /> Chargement…</p>}
              {!loadingWatch && selectedWatch.providers && (
                <>
                  {['flatrate', 'free', 'ads', 'rent', 'buy'].map(kind => {
                    const arr = selectedWatch.providers[kind];
                    if (!arr || arr.length === 0) return null;
                    const labels = { flatrate: 'Avec abonnement', free: 'Gratuit', ads: 'Avec pubs', rent: 'En location', buy: "À l'achat" };
                    return (
                      <div key={kind}>
                        <p className="text-xs uppercase text-gray-400 mb-2 font-semibold">{labels[kind]}</p>
                        <div className="grid grid-cols-4 gap-2">
                          {arr.map(p => (
                            <div key={p.provider_id} className="bg-white/5 border border-white/10 rounded-xl p-2 text-center">
                              {p.logo_url && <img src={p.logo_url} alt={p.name} className="w-10 h-10 mx-auto rounded" />}
                              <p className="text-[10px] mt-1 truncate">{p.name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {selectedWatch.providers?.link && (
                    <a href={selectedWatch.providers.link} target="_blank" rel="noopener noreferrer" className="block w-full text-center py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-xl text-sm font-medium border border-cyan-500/30">
                      Voir sur JustWatch ↗
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ===== NEWS =====
export const NewsSection = () => {
  const { t, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`/api/news/feed?lang=${language}`).then(r => setNews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [language]);

  const play = async (article) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (playingId === article.id) { setPlayingId(null); return; }
    setLoadingId(article.id);
    try {
      const text = `${article.title}. ${article.description || ''}`;
      const res = await axios.post(`/api/news/${article.id}/tts`, { text });
      if (res.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audioRef.current = audio;
        audio.play();
        setPlayingId(article.id);
        audio.onended = () => setPlayingId(null);
      }
    } catch { toast.error('Erreur audio'); }
    finally { setLoadingId(null); }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-orange-400" />
          {t('explorer.news') || 'Actualités'}
        </h2>
      </div>
      {news.length === 0 ? (
        <p className="text-gray-500 text-sm">Chargement des actualités...</p>
      ) : (
        <HorizontalScrollSection>
          {news.map((article) => (
            <div key={article.id} className="flex-shrink-0 w-72 snap-start bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all group">
              {article.image_url && (
                <div className="relative h-36 overflow-hidden">
                  <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {article.source_name && <span className="absolute bottom-2 left-2 text-[10px] bg-black/50 px-2 py-0.5 rounded-full text-white/80">{article.source_name}</span>}
                </div>
              )}
              <div className="p-3.5">
                <h3 className="font-semibold text-sm line-clamp-3 mb-1.5 leading-snug">{article.title}</h3>
                {article.description && (
                  <>
                    <p
                      className={`text-xs text-gray-400 mb-1 ${expanded[article.id] ? '' : 'line-clamp-[10]'}`}
                      data-testid={`news-desc-${article.id}`}
                    >
                      {article.description}
                    </p>
                    {article.description.length > 280 && (
                      <button
                        type="button"
                        onClick={() => setExpanded(p => ({ ...p, [article.id]: !p[article.id] }))}
                        className="text-[11px] text-orange-300 hover:text-orange-200 font-semibold mb-2"
                        data-testid={`news-toggle-${article.id}`}
                      >
                        {expanded[article.id] ? dcT(language, 'showLess') : dcT(language, 'showMore')}
                      </button>
                    )}
                  </>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => play(article)}
                    disabled={loadingId === article.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${playingId === article.id ? 'bg-orange-500 text-white' : 'bg-white/10 hover:bg-orange-500/20 text-gray-300'}`}
                  >
                    {loadingId === article.id ? <Loader2 className="w-3 h-3 animate-spin" /> : playingId === article.id ? <Volume2 className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                    {playingId === article.id ? 'Stop' : 'Écouter'}
                  </button>
                  {article.source_url && (
                    <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs text-gray-300">
                      <ExternalLink className="w-3 h-3" /> Source
                    </a>
                  )}
                  <button
                    onClick={() => setCommentsFor({ type: 'pop_culture_news', id: article.id })}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-fuchsia-500/20 rounded-full text-xs text-gray-300"
                    data-testid={`news-comment-btn-${article.id}`}
                  >
                    <MessageCircle className="w-3 h-3" /> {dcT(language, 'comments')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </HorizontalScrollSection>
      )}
      {commentsFor && (
        <CommentsModal
          contentType={commentsFor.type}
          contentId={commentsFor.id}
          lang={language}
          onClose={() => setCommentsFor(null)}
        />
      )}
    </section>
  );
};

// ===== FREE FILMS =====
export const FreeFilmsSection = () => {
  const { t, language } = useLanguage();
  const [films, setFilms] = useState([]);
  const [visible, setVisible] = useState(true);
  const [playbackMode, setPlaybackMode] = useState('inapp');

  useEffect(() => {
    // Visible for every language — only the admin toggle can hide it.
    axios.get('/api/settings/films-libres-visibility')
      .then(r => setVisible(!!r.data?.visible))
      .catch(() => setVisible(true));
    axios.get('/api/settings/films-libres-playback')
      .then(r => setPlaybackMode(r.data?.mode === 'browser' ? 'browser' : 'inapp'))
      .catch(() => {});
  }, [language]);

  useEffect(() => {
    if (!visible) return;
    axios.get('/api/explorer/free-films', { params: { lang: language } })
      .then(r => setFilms(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [language, visible]);

  if (!visible) return null;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Film className="w-6 h-6 text-cyan-400" />
          {t('explorer.freeFilms') || 'Films libres de droit'}
        </h2>
      </div>
      {films.length === 0 ? (
        <p className="text-gray-500 text-sm">Chargement...</p>
      ) : (
        <HorizontalScrollSection>
          {films.map((film) => <FreeFilmCard key={film.id} film={film} playbackMode={playbackMode} />)}
        </HorizontalScrollSection>
      )}
    </section>
  );
};
